import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    vehicle_id: null,
    registration_no: "",
    make_model: "",
    capacity: 0,
    gps_device_id: "",
    vehicle_code: "",
    has_trip_assignment: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  // Switch bus code modal state
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [switchVehicle1, setSwitchVehicle1] = useState("");
  const [switchVehicle2, setSwitchVehicle2] = useState("");
  const [switchError, setSwitchError] = useState("");
  const [switchSuccess, setSwitchSuccess] = useState("");
  const [switchLoading, setSwitchLoading] = useState(false);

  const normalizeValue = useCallback(
    (value) => (value || "").toString().trim().toLowerCase(),
    [],
  );
  const isVehicleActive = useCallback((vehicle) => {
    return (
      vehicle.is_active === true ||
      vehicle.is_active === 1 ||
      vehicle.is_active === "1"
    );
  }, []);

  const isTripAssignedToVehicle = useCallback(
    (trip, vehicle) => {
      if (["completed", "cancelled"].includes(normalizeValue(trip.status)))
        return false;

      const tripVehicleId = normalizeValue(trip.vehicle_id);
      const vehicleId = normalizeValue(vehicle.vehicle_id);
      const vehicleCode = normalizeValue(vehicle.vehicle_code);
      const tripBusNumber = normalizeValue(trip.bus_number);
      const tripVehicleCode = normalizeValue(trip.vehicle_code);

      return (
        (vehicleId && tripVehicleId === vehicleId) ||
        (vehicleCode &&
          (tripBusNumber === vehicleCode || tripVehicleCode === vehicleCode))
      );
    },
    [normalizeValue],
  );

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const [vehiclesRes, tripsRes] = await Promise.all([
        apiFetch("/vehicles"),
        apiFetch("/trips").catch(() => ({ data: [] })),
      ]);
      const trips = tripsRes.data || [];
      const rows = (vehiclesRes.data || []).map((vehicle) => ({
        ...vehicle,
        has_trip_assignment:
          Number(vehicle.has_trip_assignment) === 1 ||
          trips.some((trip) => isTripAssignedToVehicle(trip, vehicle)),
      }));
      setVehicles(rows);
    } catch {
      toast.error("Failed loading fleet table");
    } finally {
      setLoading(false);
    }
  }, [isTripAssignedToVehicle]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const compareByBusId = (a, b) => {
    const idA = parseInt(a.bus_id) || 0;
    const idB = parseInt(b.bus_id) || 0;
    return idA - idB;
  };

  const searchedVehicles = vehicles.filter((v) => {
    const query = searchQuery.toLowerCase();
    return (
      (v.vehicle_code && v.vehicle_code.toLowerCase().includes(query)) ||
      (v.registration_no && v.registration_no.toLowerCase().includes(query)) ||
      (v.bus_id && String(v.bus_id).toLowerCase().includes(query))
    );
  });

  const activeVehicles = searchedVehicles
    .filter((v) => isVehicleActive(v))
    .sort(compareByBusId);
  const disabledVehicles = searchedVehicles
    .filter((v) => !isVehicleActive(v))
    .sort(compareByBusId);
  const filteredVehicles =
    statusFilter === "active"
      ? activeVehicles
      : statusFilter === "disabled"
        ? disabledVehicles
        : [...activeVehicles, ...disabledVehicles];

  // Pagination
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredVehicles.length / ITEMS_PER_PAGE),
  );
  const paginatedVehicles = filteredVehicles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(totalPages, p + 1));

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const isGpsOnlyEdit = !!formData.vehicle_id && !!formData.has_trip_assignment;

  const handleEditOpen = (v) => {
    setFormData({
      vehicle_id: v.vehicle_id,
      registration_no: v.registration_no,
      make_model: v.make_model,
      capacity: v.capacity,
      gps_device_id: v.gps_device_id,
      vehicle_code: v.vehicle_code || "",
      has_trip_assignment: !!v.has_trip_assignment,
    });
    setIsModalOpen(true);
  };

  const handleAddOpen = () => {
    setFormData({
      vehicle_id: null,
      registration_no: "",
      make_model: "",
      capacity: 0,
      gps_device_id: "",
      vehicle_code: "",
      has_trip_assignment: false,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = isGpsOnlyEdit
        ? { gps_device_id: formData.gps_device_id }
        : { ...formData, capacity: parseInt(formData.capacity) };
      if (formData.vehicle_id) {
        await apiFetch(`/vehicles/${formData.vehicle_id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.success("Vehicle updated!");
      } else {
        await apiFetch("/vehicles", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.success("Vehicle created!");
      }
      setIsModalOpen(false);
      loadVehicles();
    } catch (err) {
      const msg = err.message || "";
      if (err.code === "DUPLICATE_CODE" || msg.includes("already in use"))
        toast.error(msg);
      else if (err.code === "VEHICLE_ASSIGNED_TO_TRIP") toast.error(msg);
      else toast.error(msg || "Failed saving vehicle");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (v) => {
    const action = isVehicleActive(v) ? "Disable" : "Enable";
    if (!window.confirm(`${action} vehicle ${v.registration_no}?`)) return;
    try {
      await apiFetch(`/vehicles/${v.vehicle_id}/toggle`, { method: "PATCH" });
      toast.success(`${action}d successfully`);
      loadVehicles();
    } catch (err) {
      // Show specific error message from backend for 409 status
      if (err.status === 409 || err.code === "VEHICLE_HAS_TRIPS") {
        toast.error(
          err.message ||
            "Cannot disable: Vehicle has active trips or route assignments.",
        );
      } else {
        const msg = err.message || `Failed to ${action.toLowerCase()} vehicle`;
        toast.error(msg);
      }
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await apiFetch(`/vehicles/${vehicleId}`, { method: "DELETE" });
      toast.success("Deleted successfully");
      loadVehicles();
    } catch (err) {
      // Show specific error message from backend for 409 status
      if (err.status === 409) {
        toast.error(
          err.message ||
            "Cannot delete: Vehicle has active trips or route assignments.",
        );
      } else {
        toast.error(
          err.message?.includes("assigned to")
            ? "Cannot delete, currently in use"
            : err.message || "Failed deleting vehicle",
        );
      }
    }
  };

  const handleSwitchBusCode = async () => {
    setSwitchError("");
    setSwitchSuccess("");

    if (!switchVehicle1 || !switchVehicle2) {
      setSwitchError("Please select both vehicles");
      return;
    }

    if (switchVehicle1 === switchVehicle2) {
      setSwitchError("Please select two different vehicles");
      return;
    }

    setSwitchLoading(true);
    try {
      await apiFetch("/vehicles/switch-code", {
        method: "POST",
        body: JSON.stringify({
          vehicle_id_1: switchVehicle1,
          vehicle_id_2: switchVehicle2,
        }),
      });

      setSwitchSuccess("Bus codes switched successfully!");
      loadVehicles(); // refresh list
      // Close modal immediately and reset state
      setShowSwitchModal(false);
      setSwitchVehicle1("");
      setSwitchVehicle2("");
      setSwitchError("");
      setSwitchSuccess("");
    } catch (err) {
      setSwitchError(err.message || "Failed to switch bus codes");
    } finally {
      setSwitchLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 className="page-title text-gradient" style={{ margin: 0 }}>
          Vehicle Management
        </h1>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by bus code or registration..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="form-control"
            style={{ width: "250px", height: "40px" }}
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="form-control"
            style={{ width: "120px", height: "40px", color: "#000" }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
          <button
            className="btn-primary"
            onClick={handleAddOpen}
            style={{ height: "40px" }}
          >
            + Add Vehicle
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowSwitchModal(true)}
            style={{ height: "40px" }}
          >
            🔄 Switch Bus Code
          </button>
        </div>
      </div>

      <div
        className="data-table-container glass-panel"
        style={{
          overflowX: "visible",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <table className="data-table" style={{ fontSize: "0.9rem", flex: 1 }}>
          <thead>
            <tr style={{ height: "48px" }}>
              <th style={{ padding: "10px 16px" }}>Bus ID</th>
              <th style={{ padding: "10px 16px" }}>Registration</th>
              <th style={{ padding: "10px 16px" }}>Bus Code</th>
              <th style={{ padding: "10px 16px" }}>Make/Model</th>
              <th style={{ padding: "10px 16px" }}>Capacity</th>
              <th style={{ padding: "10px 16px" }}>GPS Device ID</th>
              <th style={{ padding: "10px 16px" }}>Status</th>
              <th style={{ padding: "10px 16px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="8"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  Loading vehicles...
                </td>
              </tr>
            ) : vehicles.length === 0 ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="8"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  No vehicles found.
                </td>
              </tr>
            ) : filteredVehicles.length === 0 ? (
              <tr style={{ height: "48px" }}>
                <td
                  colSpan="8"
                  style={{ textAlign: "center", padding: "10px 16px" }}
                >
                  No vehicles match your search.
                </td>
              </tr>
            ) : (
              paginatedVehicles.map((v) => {
                const isActive = isVehicleActive(v);
                return (
                  <tr
                    key={v.vehicle_id}
                    style={{
                      opacity: isActive ? 1 : 0.62,
                      height: "48px",
                      background: isActive
                        ? "transparent"
                        : "rgba(148, 163, 184, 0.12)",
                    }}
                  >
                    <td
                      style={{
                        fontWeight: 700,
                        color: "var(--text-main)",
                        padding: "10px 16px",
                      }}
                    >
                      {v.bus_id}
                    </td>
                    <td style={{ fontWeight: 600, padding: "10px 16px" }}>
                      {v.registration_no}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      {v.vehicle_code ? (
                        <span
                          className={
                            isActive
                              ? "badge badge-active"
                              : "badge badge-inactive"
                          }
                        >
                          {v.vehicle_code}
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "var(--text-muted)",
                            fontStyle: "italic",
                          }}
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "10px 16px" }}>{v.make_model}</td>
                    <td style={{ padding: "10px 16px" }}>{v.capacity}</td>
                    <td style={{ padding: "10px 16px" }}>
                      <code
                        style={{
                          background: "var(--bg-alt)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                        }}
                      >
                        {v.gps_device_id}
                      </code>
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <span
                        className={`badge ${isActive ? "badge-active" : "badge-inactive"}`}
                      >
                        {isActive ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <div className="table-actions">
                        <button
                          className="btn-secondary"
                          onClick={() => handleEditOpen(v)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleToggle(v)}
                        >
                          {isActive ? "Disable" : "Enable"}
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleDelete(v.vehicle_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredVehicles.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              padding: "16px",
              borderTop: "1px solid var(--border-color)",
            }}
          >
            <button
              className="btn-secondary"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              ← Previous
            </button>
            <span style={{ color: "var(--text-muted)" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {formData.vehicle_id ? "Edit Vehicle" : "Add Vehicle"}
              </h2>
              <button
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {isGpsOnlyEdit && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    border: "1px solid var(--border-color)",
                    borderRadius: "6px",
                    color: "var(--text-muted)",
                    background: "var(--bg-alt)",
                  }}
                >
                  This vehicle is assigned to a trip. Only the GPS Device ID can
                  be edited.
                </div>
              )}
              {!isGpsOnlyEdit && (
                <>
                  <div className="form-group">
                    <label className="form-label">
                      Vehicle Code (Logical ID)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="vehicle_code"
                      value={formData.vehicle_code}
                      onChange={handleChange}
                      placeholder="e.g. Bus 1, BUS-A (must be unique among active vehicles)"
                    />
                    <small
                      style={{
                        color: "var(--text-muted)",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      Used to identify this bus in trips and student
                      assignments. Can be reused after disabling.
                    </small>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Registration No</label>
                      <input
                        type="text"
                        className="form-control"
                        name="registration_no"
                        value={formData.registration_no}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Capacity</label>
                      <input
                        type="number"
                        className="form-control"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                        required
                        min="1"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Make/Model</label>
                    <input
                      type="text"
                      className="form-control"
                      name="make_model"
                      value={formData.make_model}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">GPS Device ID</label>
                <input
                  type="text"
                  className="form-control"
                  name="gps_device_id"
                  value={formData.gps_device_id}
                  onChange={handleChange}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save Vehicle"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Switch Bus Code Modal */}
      {showSwitchModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "30px",
              borderRadius: "12px",
              width: "400px",
              maxWidth: "90%",
            }}
          >
            <h3 style={{ marginBottom: "20px", color: "#000" }}>
              Switch Bus Code
            </h3>
            <p
              style={{
                marginTop: "0",
                marginBottom: "18px",
                color: "var(--text-muted)",
                fontSize: "0.95rem",
              }}
            >
              Only vehicles without an active, returning, or scheduled trip
              assignment can be switched.
            </p>

            {switchError && (
              <p
                style={{ color: "red", fontSize: "13px", marginBottom: "10px" }}
              >
                {switchError}
              </p>
            )}

            {switchSuccess && (
              <p
                style={{
                  color: "green",
                  fontSize: "13px",
                  marginBottom: "10px",
                }}
              >
                {switchSuccess}
              </p>
            )}

            <label
              style={{
                fontSize: "13px",
                color: "#666",
                display: "block",
                marginBottom: "4px",
              }}
            >
              First Vehicle
            </label>
            <select
              value={switchVehicle1}
              onChange={(e) => {
                setSwitchVehicle1(e.target.value);
                setSwitchVehicle2("");
              }}
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <option value="">Select vehicle...</option>
              {vehicles
                .filter((v) => isVehicleActive(v) && !v.has_trip_assignment)
                .map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>
                    {v.registration_no} — {v.vehicle_code || "No Code"}
                  </option>
                ))}
            </select>

            <label
              style={{
                fontSize: "13px",
                color: "#666",
                display: "block",
                marginBottom: "4px",
              }}
            >
              Second Vehicle
            </label>
            <select
              value={switchVehicle2}
              onChange={(e) => setSwitchVehicle2(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <option value="">Select vehicle...</option>
              {vehicles
                .filter(
                  (v) =>
                    isVehicleActive(v) &&
                    !v.has_trip_assignment &&
                    v.vehicle_id !== switchVehicle1,
                )
                .map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>
                    {v.registration_no} — {v.vehicle_code || "No Code"}
                  </option>
                ))}
            </select>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowSwitchModal(false);
                  setSwitchVehicle1("");
                  setSwitchVehicle2("");
                  setSwitchError("");
                  setSwitchSuccess("");
                }}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSwitchBusCode}
                disabled={switchLoading}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background: "#000",
                  color: "#fff",
                  cursor: switchLoading ? "not-allowed" : "pointer",
                  opacity: switchLoading ? 0.7 : 1,
                }}
              >
                {switchLoading ? "Switching..." : "Switch"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
